<template>
  <div id="kiosk-search-page" class="row w-100 justify-content-center">
    <div class="col-12 col-md-9 col-lg-8 text-left">
        <h1 class="mb-2">Let's find your appointment.</h1>
        <p class="mb-4">Type your last name to find today's appointment at this salon.</p>

        <div class="d-flex align-items-center bg-white kiosk-search-bar"
             style="border:1px solid var(--form-color); border-radius:2rem; padding:.85rem 1.4rem .85rem .75rem;">
          <div class="kiosk-search-icon mr-1 d-flex align-items-center justify-content-center">
            <i v-if="!searching" class="fa fas fa-search" style="color:#888;"></i>
            <span v-else class="inline-loader kiosk-search-loader"></span>
          </div>
          <input class="form-control border-0 p-0" v-model="lastName" placeholder="Enter your last name..."
                 @input="search"
                 style="font-size:1.125rem; background:transparent; box-shadow:none;"/>
        </div>

        <div v-if="tooShort" class="mt-4 kiosk-empty-results">
          <validation-message :message="'Please enter at least 2 letters.'"></validation-message>
        </div>
        <div v-else-if="error" class="mt-3 text-danger p2">{{ error }}</div>

        <div v-else-if="results.length" class="mt-3">
          <div v-for="g in results" :key="g.appointmentGroupId"
               class="d-flex align-items-center p-3 bg-white mb-2 kiosk-result-row"
               style="border:1px solid #EEE; border-radius:14px; cursor:pointer; box-shadow:0 4px 16px rgba(0,0,0,.08);"
               @click="$emit('pick', g)">
            <div class="d-flex align-items-center justify-content-center font-weight-bold mr-3 kiosk-result-avatar"
                 style="width:42px; height:42px; border-radius:50%; background:#E8F4F8; color:var(--tertiary-color);">
              {{ initials(g.clientFullName) }}
            </div>
            <div>
              <div class="font-weight-bold kiosk-result-name">{{ g.clientFullName }}</div>
              <div class="p2 text-muted kiosk-result-pets">{{ g.petNames.join(' &amp; ') }}</div>
            </div>
            <div class="ml-auto text-right p2 text-muted">
              <div class="font-weight-bold kiosk-result-time" style="color:var(--default-color); font-size:.9375rem;">{{ formatTime(g.startLocal) }}</div>
            </div>
          </div>
        </div>

        <div v-else-if="attempted && !results.length" class="mt-4 kiosk-empty-results">
          <validation-message :message="'No appointments found for that last name today.'"></validation-message>
        </div>

    </div>
  </div>
</template>

<script>
import { WebApi } from '@/services/WebApi'
import { ErrorHelper } from '@/helpers/ErrorHelper'
import ValidationMessage from '@/components/ErrorValidation/ValidationMessage.vue'

export default {
  name: 'KioskSearch',
  components: { 'validation-message': ValidationMessage },
  data () {
    return { lastName: '', results: [], searching: false, attempted: false, error: '', debounce: null }
  },
  computed: {
    tooShort () {
      const trimmed = (this.lastName || '').trim()
      return trimmed.length > 0 && trimmed.length < 2
    }
  },
  methods: {
    initials (name) {
      return (name || '').split(' ').map(s => s[0] || '').join('').slice(0, 2).toUpperCase()
    },
    formatTime (iso) {
      try {
        const d = new Date(iso)
        return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
      } catch (e) { return iso }
    },
    search () {
      const self = this
      clearTimeout(self.debounce)
      if (!self.lastName || self.lastName.trim().length < 2) {
        self.results = []
        self.attempted = false
        return
      }
      self.debounce = setTimeout(() => {
        self.searching = true
        self.error = ''
        WebApi.get('Kiosk/Appointments/Search', { params: { lastName: self.lastName.trim() } })
          .then(response => { self.results = response.data || [] })
          .catch(error => {
            const status = error.response && error.response.status
            if (status === 401 || status === 403) {
              self.$emit('unauthorized')
            } else {
              self.error = ErrorHelper.getError(error)
            }
          })
          .finally(() => { self.searching = false; self.attempted = true })
      }, 1000)
    }
  }
}
</script>

<style scoped>
#kiosk-search-page .kiosk-search-icon {
  width: 44px;
  height: 40px;
  flex: 0 0 44px;
}
#kiosk-search-page .kiosk-empty-results,
#kiosk-search-page .kiosk-empty-results ::v-deep label {
  font-size: 1.25rem !important;
}
#kiosk-search-page .kiosk-search-loader {
  width: 100%;
  height: 100%;
}
@media (max-width: 576px) {
  #kiosk-search-page h1 { font-size: 1.375rem !important; }
  #kiosk-search-page p,
  #kiosk-search-page .p2 { font-size: .8125rem; }
  #kiosk-search-page input.form-control { font-size: .9375rem !important; }
  #kiosk-search-page .kiosk-search-bar { padding: .55rem 1rem .55rem .5rem !important; }
  #kiosk-search-page .kiosk-search-icon { width: 32px; height: 28px; flex: 0 0 32px; }
  #kiosk-search-page .kiosk-result-row { padding: .5rem !important; }
  #kiosk-search-page .kiosk-result-avatar { width: 32px !important; height: 32px !important; font-size: .8125rem; }
  #kiosk-search-page .kiosk-result-name { font-size: .875rem; }
  #kiosk-search-page .kiosk-result-pets { font-size: .75rem; }
  #kiosk-search-page .kiosk-result-time { font-size: .75rem !important; }
  #kiosk-search-page .kiosk-result-checkedin { font-size: .6875rem; }
  #kiosk-search-page .kiosk-empty-results,
  #kiosk-search-page .kiosk-empty-results ::v-deep label { font-size: .9375rem !important; }
}
</style>
